import { ChatMessage, ConversationRoles, MistralClient } from '@src/lib/mistral-api'
// Removed unused import { error } from 'util'

// src/index.ts
export const client = new MistralClient()
export type AIToolChoice = 'auto' | 'any' | 'none' | { type: 'function'; function: { name: string } }

function getFirstResponseMessage (response: any): ChatMessage | null {
  const choice = response && Array.isArray(response.choices) ? response.choices[0] : null
  return choice && choice.message ? choice.message : null
}

export class AiChatBot {
  private tools: any[]
  private toolHandlers: Record<string, (args: any) => string | Promise<string>>
  private model: string

  constructor (tools: any[], toolHandlers: Record<string, (args: any) => string | Promise<string>>, model: string = 'mistral-medium-2505') {
    this.tools = tools
    this.toolHandlers = toolHandlers
    this.model = model
  }

  public async answerConversation (messages: ChatMessage[]) {
    console.log('--- Standard Chat Completion ---')
    let currentMessages = [...messages]
    for (;;) {
      try {
        const response = await client.createChatCompletion({
          model: this.model,
          messages: currentMessages,
          tools: this.tools,
          tool_choice: 'auto'
        })
        const message = getFirstResponseMessage(response)
        if (!message) return ''
        if (message.tool_calls && message.tool_calls.length > 0) {
          const toolResponses: ChatMessage[] = []
          for (const toolCall of message.tool_calls) {
            const name = toolCall.function && toolCall.function.name
            let result = ''
            if (!name) {
              result = 'Error: Invalid tool call - missing function name'
            } else if (this.toolHandlers[name]) {
              const handler = this.toolHandlers[name]
              let argsObj
              try {
                argsObj = toolCall.function.arguments ? JSON.parse(toolCall.function.arguments) : {}
              } catch (e) {
                result = `Error: Invalid arguments - ${e.message}`
              }
              if (argsObj) {
                try {
                  result = await handler(argsObj)
                } catch (e) {
                  result = `Error: Execution failed - ${e.message}`
                }
              }
            } else {
              result = `Error: No handler found for tool: ${name}`
            }
            toolResponses.push({
              role: ConversationRoles.TOOL,
              content: result,
              tool_call_id: toolCall.id
            })
          }
          currentMessages = [...currentMessages, message, ...toolResponses]
          continue
        } else {
          return message.content || ''
        }
      } catch (error) {
        console.error('Error during standard chat completion:', error)
        throw error
      }
    }
  }

  public async answerConversationStream (
      messages: ChatMessage[],
      onContentDelta: (delta: string) => void
  ): Promise<any> {
    console.log('\n\n--- Streaming Chat Completion ---')
    try {
      let accumulatedContent = ''
      let accumulatedToolCalls: any[] = []
      await client.createChatCompletionStream({
        model: this.model,
        messages,
        tools: this.tools,
        tool_choice: 'auto'
      }, (chunk) => {
        const choice = chunk && Array.isArray(chunk.choices) ? chunk.choices[0] : null
        const delta = choice && choice.delta ? choice.delta : null
        if (!delta) return
        if (delta.content) {
          onContentDelta(delta.content)  // Call the provided callback for UI updates
          accumulatedContent += delta.content
        }
        if (delta.tool_calls) {
          // Accumulate tool call deltas (Mistral sends them incrementally)
          delta.tool_calls.forEach((toolDelta: any) => {
            const idx = toolDelta.index
            if (!accumulatedToolCalls[idx]) {
              accumulatedToolCalls[idx] = { id: '', type: 'function', function: { name: '', arguments: '' } }
            }
            const toolFunction = toolDelta.function || {}
            if (toolDelta.id) {
              accumulatedToolCalls[idx].id = toolDelta.id
            }
            if (toolFunction.name) {
              accumulatedToolCalls[idx].function.name += toolFunction.name
            }
            if (toolFunction.arguments) {
              accumulatedToolCalls[idx].function.arguments += toolFunction.arguments
            }
          })
        }
      })
      // After stream ends, check for accumulated tool_calls
      if (accumulatedToolCalls.length > 0) {
        // NEW: Return the accumulated content immediately (no recursion yet; caller will handle update)
        return { content: accumulatedContent, tool_calls: accumulatedToolCalls }  // Return an object to signal tool calls
      } else {
        return accumulatedContent
      }
    } catch (error) {
      console.error('Error during streaming chat completion:', error)
      throw error
    }
  }

  public async answerConversationStreamWithUsage (
      messages: ChatMessage[],
      onContentDelta: (delta: string) => void,
      toolChoice: AIToolChoice = 'auto',
      streamContent: boolean = true
  ): Promise<{ result: any, usage?: any }> {
    console.log('\n\n--- Streaming Chat Completion with Usage ---')

    // For token tracking, we'll use regular completion to get usage info
    // This ensures we can track tokens properly
    try {
      const request = {
        model: this.model,
        messages,
        tools: this.tools,
        tool_choice: toolChoice
      }
      let response
      try {
        response = await client.createChatCompletion(request)
      } catch (error) {
        if (!error || error.status !== 400 || toolChoice === 'auto') {
          throw error
        }

        console.warn(`Tool choice ${JSON.stringify(toolChoice)} was rejected; retrying with auto`)
        response = await client.createChatCompletion({
          ...request,
          tool_choice: 'auto'
        })
      }

      const message = getFirstResponseMessage(response)
      if (!message) {
        return {
          result: '',
          usage: response && response.usage
        }
      }

      // Simulate streaming by calling onContentDelta with the complete content
      if (message.content && streamContent) {
        // Split content into words to simulate streaming
        const words = message.content.split(' ')
        for (let i = 0; i < words.length; i++) {
          onContentDelta((i > 0 ? ' ' : '') + words[i])
          // Small delay to simulate streaming
          await new Promise(resolve => setTimeout(resolve, 20))
        }
      }

      // Return both result and usage information
      const result = message.tool_calls && message.tool_calls.length > 0
        ? { content: message.content, tool_calls: message.tool_calls }
        : message.content || ''

      return {
        result,
        usage: response.usage
      }
    } catch (error) {
      console.error('Error during streaming chat completion with usage:', error)
      throw error
    }
  }
}

// Example usage:
// const tools = [
//   {
//     type: 'function',
//     function: {
//       name: 'close_chat',
//       description: 'Call this function to end the conversation when the user explicitly says they don\'t need further help, want to close the chat, or something similar like \'I\'m done\' or \'No more questions\'.',
//       parameters: {
//         type: 'object',
//         properties: {
//           farewell_message: {
//             type: 'string',
//             description: 'An optional polite message to send before closing, e.g., \'Goodbye!\''
//           }
//         },
//         required: []
//       }
//     }
//   }
//   // Add more tools schemas here
// ];
// const toolHandlers = {
//   close_chat: (args) => args.farewell_message || 'Chat closed.'
//   // Add handlers for other tools, e.g., 'get_weather': (args) => `Weather in ${args.city} is sunny.`
// };
// const bot = new AiChatBot(tools, toolHandlers);
// // Then call bot.answerConversation(messages) or bot.answerConversationStream(messages)
// src/types.ts
